/**
 * RBACContext - Unified Role-Based Access Control Context
 *
 * This context provides the SINGLE SOURCE OF TRUTH for all RBAC data.
 * It fetches from the backend /api/v1/admin/me endpoint, ensuring:
 * - Consistent role values across all components
 * - Single API call for all RBAC data
 * - Easy debugging (single logging point)
 * - Backend handles all complex role logic
 *
 * Key features:
 * - Single `rbacReady` flag that waits for ALL RBAC data to load
 * - All derived values (role, isSuperadmin, companies, permissions, etc.) from backend
 * - Refresh mechanism for re-fetching after role changes
 *
 * The RBACProvider component lives in ./RBACProvider — this file holds the
 * types, context object, and useRBAC hook (react-refresh/only-export-components).
 */

import { createContext, useContext } from 'react';
import type { IndustryType, CompanyThemeJson } from '@/types';

export type UserRole = 'user' | 'group_admin' | 'company_admin' | 'admin' | 'superadmin';

// User data from backend /admin/me response
export interface AdminUser {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  is_internal: boolean;
  default_company_id?: string;
  default_group_id?: string;
}

// Company data from backend /admin/me response
export interface UserCompany {
  id: string;
  name: string;
  slug: string;
  is_internal: boolean;
  industry?: IndustryType;
  role: string;
  logo_url?: string | null;
  theme_json?: CompanyThemeJson | null;
}

// Membership data from backend /admin/me response
export interface UserMembership {
  id: string;
  company_id: string;
  group_id: string | null;
  role: string;
  company_name: string;
  group_name?: string;
}

// Profile interface
export interface UserProfile {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  is_internal: boolean;
  default_company_id: string | null;
  default_group_id: string | null;
}

export interface RBACContextType {
  // Loading state - true only when ALL RBAC data is ready
  rbacReady: boolean;
  loading: boolean;

  // Role data
  role: UserRole | null;
  isGroupAdmin: boolean;
  isCompanyAdmin: boolean;
  isAdmin: boolean;
  isSuperadmin: boolean;

  // Profile data (legacy interface for backward compatibility)
  profile: UserProfile | null;
  isInternal: boolean;

  // Company data
  company: UserCompany | null;
  companies: UserCompany[];

  // NEW: Full user object from backend
  user: AdminUser | null;

  // NEW: Memberships from backend
  memberships: UserMembership[];

  // NEW: Permissions from backend
  permissions: string[];

  // T-068 P3: server-computed feature-visibility flags. report_subscriptions_admin
  // = superadmin or a company-wide admin/company_admin (reportAdminScope). The API
  // is still the enforcement point; this only gates sidebar/UI visibility.
  capabilities: UserCapabilities;

  // Refresh function (e.g., after role change)
  refreshRBAC: () => Promise<void>;
}

/** Coarse server-computed feature flags surfaced on /admin/me. */
export interface UserCapabilities {
  report_subscriptions_admin: boolean;
  /**
   * "Deployed but not billed" reconciliation. Internal staff + superadmin only —
   * it names the units HT is failing to bill a customer for, so a company_admin
   * of that customer must not see it. The API is the enforcement point.
   */
  unbilled_deployments_view: boolean;
}

/** Names of the capability flags, for props that gate on one. */
export type CapabilityName = keyof UserCapabilities;

// Export context for components that need safe optional access (e.g., check-in wizard)
export const RBACContext = createContext<RBACContextType | undefined>(undefined);

/**
 * Hook to access RBAC context
 */
export function useRBAC(): RBACContextType {
  const context = useContext(RBACContext);
  if (context === undefined) {
    throw new Error('useRBAC must be used within an RBACProvider');
  }
  return context;
}
